import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '@/store/base-query';
import type { OrgProfile } from '@/types';
import type {
  PersonaTab,
  SearchProfile,
  SearchProfileDraft,
} from './search-profiles.types';

const headers = (orgId: string) => ({ 'X-Cleanlist-Expected-Org': orgId });

// Organization is part of every cache key. The server derives scope from the
// signed-in session, never from a caller-supplied organization parameter.
export const searchProfilesApi = createApi({
  reducerPath: 'searchProfilesApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['SearchProfiles'],
  endpoints: (builder) => ({
    getWorkspaceSearchProfile: builder.query<OrgProfile, string>({
      query: (orgId) => ({ url: '/agents/profile', headers: headers(orgId) }),
    }),
    getSearchProfilesStatus: builder.query<{ enabled: boolean }, string>({
      query: (orgId) => ({
        url: '/search-profiles/status',
        headers: headers(orgId),
      }),
      keepUnusedDataFor: 0,
    }),
    getSearchProfiles: builder.query<SearchProfile[], string>({
      query: (orgId) => ({ url: '/search-profiles', headers: headers(orgId) }),
      providesTags: (_result, _error, orgId) => [
        { type: 'SearchProfiles', id: orgId },
      ],
    }),
    createSearchProfile: builder.mutation<
      SearchProfile,
      { orgId: string; draft: SearchProfileDraft }
    >({
      query: ({ orgId, draft }) => ({
        headers: headers(orgId),
        url: '/search-profiles',
        method: 'POST',
        body: draft,
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'SearchProfiles', id: orgId },
      ],
    }),
    updateSearchProfile: builder.mutation<
      SearchProfile,
      { orgId: string; id: string; version: number; draft: SearchProfileDraft }
    >({
      query: ({ orgId, id, version, draft }) => ({
        headers: headers(orgId),
        url: `/search-profiles/${id}`,
        method: 'PUT',
        body: { ...draft, version },
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'SearchProfiles', id: orgId },
      ],
    }),
    // One-time upload of this browser's old saved searches into personas.
    // Same body as the old /finder-searches/import; the server derives each
    // persona's criteria and skips what it already has.
    importSearchProfiles: builder.mutation<
      { imported: number; existing: number; skipped: number },
      {
        orgId: string;
        searches: Array<{
          name: string;
          tab: PersonaTab;
          filters: Record<string, unknown>;
        }>;
      }
    >({
      query: ({ orgId, searches }) => ({
        headers: headers(orgId),
        url: '/search-profiles/import',
        method: 'POST',
        body: { searches },
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'SearchProfiles', id: orgId },
      ],
    }),
    deleteSearchProfile: builder.mutation<
      void,
      { orgId: string; id: string; version: number }
    >({
      query: ({ orgId, id, version }) => ({
        headers: headers(orgId),
        url: `/search-profiles/${id}`,
        method: 'DELETE',
        params: { version },
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'SearchProfiles', id: orgId },
      ],
    }),
  }),
});

export const {
  useGetWorkspaceSearchProfileQuery,
  useGetSearchProfilesStatusQuery,
  useGetSearchProfilesQuery,
  useCreateSearchProfileMutation,
  useUpdateSearchProfileMutation,
  useDeleteSearchProfileMutation,
  useImportSearchProfilesMutation,
} = searchProfilesApi;
